import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Job, Quote } from '../../types';
import { getSupabaseJobs, getSupabaseQuotes } from '../../lib/supabaseService';
import { JobPaymentModal } from '../../components/payment/JobPaymentModal';
import {
  MapPin,
  Clock,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Wrench,
  Calendar,
  Sparkles,
  MessageSquare,
  FileText,
  CreditCard,
  Lock,
  ExternalLink,
  Image as ImageIcon,
  Video,
  UserCheck,
} from 'lucide-react';

export const JobDetailsPage: React.FC = () => {
  const { jobs, quotes, profiles, currentUser, viewParams, navigateTo, refreshJobsAndQuotes } = useApp();
  const [job, setJob] = useState<Job | null>(null);
  const [jobQuotes, setJobQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [paymentQuote, setPaymentQuote] = useState<Quote | null>(null);
  const [acceptingJob, setAcceptingJob] = useState<boolean>(false);
  const [actionBanner, setActionBanner] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const params = new URLSearchParams(window.location.search);
  const targetJobId =
    viewParams?.jobId ||
    params.get('jobId') ||
    params.get('id') ||
    window.location.pathname.split('/jobs/')[1]?.split('/')[0] ||
    '';
  const targetQuoteId = viewParams?.quoteId || params.get('quoteId') || '';
  const shouldAutoOpenPay = viewParams?.pay === 'true' || params.get('pay') === 'true';

  useEffect(() => {
    let mounted = true;
    const loadJobAndQuotes = async () => {
      setLoading(true);
      setError('');

      if (!targetJobId) {
        if (mounted) {
          setError('No job ID provided in the link.');
          setLoading(false);
        }
        return;
      }

      let foundJob = jobs.find((j) => j.id === targetJobId) || null;
      let foundQuotes = quotes.filter((q) => q.jobId === targetJobId);

      try {
        const [freshJobs, freshQuotes] = await Promise.all([
          !foundJob ? getSupabaseJobs() : Promise.resolve(null),
          getSupabaseQuotes(),
        ]);
        if (Array.isArray(freshJobs)) {
          foundJob = freshJobs.find((j) => j.id === targetJobId) || foundJob;
        }
        if (Array.isArray(freshQuotes)) {
          foundQuotes = freshQuotes.filter((q) => q.jobId === targetJobId);
        }
      } catch (e) {
        console.warn('Error fetching job details:', e);
      }

      if (mounted) {
        if (foundJob) {
          setJob(foundJob);
          setJobQuotes(foundQuotes);
        } else {
          setError('This job could not be found or may have been removed.');
        }
        setLoading(false);
      }
    };

    loadJobAndQuotes();
    return () => {
      mounted = false;
    };
  }, [targetJobId, jobs, quotes]);

  // Derived payment & assignment state
  const acceptedQuote =
    jobQuotes.find((q) => q.id === targetQuoteId) ||
    jobQuotes.find((q) => q.id === job?.acceptedQuoteId) ||
    jobQuotes.find((q) => q.status === 'accepted') ||
    (job?.selectedProfessionalId
      ? jobQuotes.find((q) => q.professionalId === job.selectedProfessionalId)
      : undefined) ||
    null;

  const rawPaymentStatus = String(
    job?.paymentStatus ||
      (job as any)?.payment_status ||
      (job?.aiScopeEstimate as any)?.lifecycle_meta?.payment_status ||
      ''
  ).toLowerCase();

  const isEscrowFunded = Boolean(
    rawPaymentStatus === 'held_in_escrow' ||
      rawPaymentStatus === 'paid' ||
      rawPaymentStatus === 'released' ||
      job?.status === 'in_progress' ||
      job?.status === 'completed_by_professional' ||
      job?.status === 'completed' ||
      job?.escrow_held === true ||
      job?.payment_reference
  );

  const isPaymentReleased = Boolean(
    rawPaymentStatus === 'released' || job?.status === 'completed' || job?.payment_released
  );

  const isAwaitingPayment =
    !isEscrowFunded &&
    (rawPaymentStatus === 'awaiting_payment' ||
      rawPaymentStatus === 'pending' ||
      job?.status === 'assigned' ||
      Boolean(acceptedQuote));

  const isCustomerOwner = Boolean(currentUser && job && currentUser.id === job.customerId);
  const isAssignedPro = Boolean(
    currentUser &&
      job &&
      currentUser.role === 'professional' &&
      (job.selectedProfessionalId === currentUser.id || acceptedQuote?.professionalId === currentUser.id)
  );

  const effectiveCheckoutQuote: Quote | null =
    acceptedQuote ||
    (job && (job.selectedProfessionalId || job.status === 'assigned')
      ? {
          id: job.acceptedQuoteId || `quote-${job.id}`,
          jobId: job.id,
          professionalId: job.selectedProfessionalId || '',
          professionalName:
            profiles.find((p) => p.id === job.selectedProfessionalId)?.fullName ||
            job.selectedProfessionalName ||
            'Assigned Professional',
          professionalAvatar:
            profiles.find((p) => p.id === job.selectedProfessionalId)?.avatarUrl || '',
          professionalCategory: job.categoryName || 'Artisan',
          professionalRating: 5.0,
          professionalReviewCount: 1,
          professionalVerification: 'verified',
          professionalYearsExp: 3,
          laborCost: Number(job.grossAmount || job.budgetEstimate || 15000),
          materialsCost: 0,
          totalAmount: Number(job.grossAmount || job.budgetEstimate || 15000),
          durationEstimate: '1 Day',
          startDate: 'After payment confirmation',
          warrantyDays: 30,
          notes: 'Accepted job quote',
          status: 'accepted',
          createdAt: job.updatedAt || job.createdAt,
        }
      : null);

  // Auto-open Paystack payment modal when customer arrives via Telegram `?pay=true` link
  useEffect(() => {
    if (shouldAutoOpenPay && job && isAwaitingPayment && effectiveCheckoutQuote && currentUser?.role === 'customer') {
      setPaymentQuote(effectiveCheckoutQuote);
    }
  }, [shouldAutoOpenPay, job?.id, isAwaitingPayment, effectiveCheckoutQuote?.id, currentUser?.role]);

  const handleProfessionalAcceptJob = async () => {
    if (!currentUser || !job) {
      navigateTo('pro_auth', { returnView: 'job_details', jobId: targetJobId });
      return;
    }
    setAcceptingJob(true);
    setActionBanner(null);
    try {
      const res = await fetch(`/api/jobs/${encodeURIComponent(job.id)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          professionalId: currentUser.id,
          professionalName: currentUser.fullName,
          amount: job.budgetEstimate || 15000,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to accept job.');
      }
      setJob((prev) =>
        prev
          ? {
              ...prev,
              status: 'assigned',
              paymentStatus: 'awaiting_payment',
              selectedProfessionalId: currentUser.id,
              acceptedQuoteId: data.quoteId || prev.acceptedQuoteId,
              grossAmount: Number(job.budgetEstimate || 15000),
            }
          : prev
      );
      setActionBanner({
        type: 'success',
        text: 'Job accepted! The customer has been sent a Telegram & in-app link to complete Paystack escrow payment. Do NOT start work until payment is confirmed.',
      });
      await refreshJobsAndQuotes().catch(() => {});
    } catch (err: any) {
      setActionBanner({
        type: 'error',
        text: err.message || 'Could not accept job right now.',
      });
    } finally {
      setAcceptingJob(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-6">
        <div className="w-12 h-12 rounded-full border-4 border-[#008751]/20 border-t-[#008751] animate-spin mb-4" />
        <p className="text-slate-600 font-medium">Loading job details...</p>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Job Unavailable</h1>
        <p className="text-slate-600 mb-6">{error || 'Job not found.'}</p>
        <button
          onClick={() => navigateTo('home')}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#008751] text-white font-semibold hover:bg-[#006b3f] transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>
      </div>
    );
  }

  const agreedOrBudgetAmount = Number(
    effectiveCheckoutQuote?.totalAmount || job.grossAmount || job.budgetEstimate || 0
  );
  const proShareAmount = Math.round(agreedOrBudgetAmount * 0.9);
  const platformCommission = agreedOrBudgetAmount - proShareAmount;
  const canViewExactAddress = isCustomerOwner || (isAssignedPro && isEscrowFunded) || currentUser?.role === 'admin';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <button
          onClick={() => {
            if (currentUser?.role === 'professional') navigateTo('pro_dashboard');
            else if (currentUser?.role === 'customer') navigateTo('customer_dashboard');
            else navigateTo('home');
          }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#008751] transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-center gap-2">
          {currentUser && (isCustomerOwner || isAssignedPro) && (
            <button
              onClick={() => navigateTo('messages', { jobId: job.id })}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Messages
            </button>
          )}
        </div>
      </div>

      {actionBanner && (
        <div
          className={`mb-5 p-4 rounded-xl border flex items-start gap-3 ${
            actionBanner.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-red-50 border-red-200 text-red-900'
          }`}
        >
          {actionBanner.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          )}
          <p className="text-sm font-medium">{actionBanner.text}</p>
        </div>
      )}

      {/* Prominent Escrow Payment / Work Gate Banner */}
      {isAwaitingPayment && (
        <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50/90 p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-200/80 text-amber-950 text-xs font-bold uppercase tracking-wide">
                <Clock className="w-3.5 h-3.5" />
                Awaiting Paystack Escrow Payment
              </div>
              <h2 className="text-lg font-extrabold text-slate-900">
                {isAssignedPro
                  ? 'Do Not Start Work Yet — Waiting for Customer Escrow Payment'
                  : 'Professional Ready — Complete Escrow Payment to Start Work'}
              </h2>
              <p className="text-sm text-slate-700">
                {isAssignedPro
                  ? 'The customer has been sent a secure payment link on Telegram and JobMatch NG. You will be notified immediately once Paystack verifies the payment.'
                  : `Fund ₦${agreedOrBudgetAmount.toLocaleString()} into JobMatch NG Escrow via Paystack. Funds are held safely and only released after you confirm the job is completed.`}
              </p>
            </div>

            {(!currentUser || currentUser.role === 'customer') && effectiveCheckoutQuote && (
              <button
                onClick={() => {
                  if (!currentUser) {
                    navigateTo('customer_auth', {
                      returnView: 'job_details',
                      jobId: job.id,
                      quoteId: effectiveCheckoutQuote.id,
                      pay: 'true',
                    });
                    return;
                  }
                  setPaymentQuote(effectiveCheckoutQuote);
                }}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#008751] text-white font-bold text-sm hover:bg-[#006b3f] transition shadow-md"
              >
                <CreditCard className="w-4 h-4" />
                Pay ₦{agreedOrBudgetAmount.toLocaleString()} Escrow Now
              </button>
            )}
          </div>
        </div>
      )}

      {isEscrowFunded && !isPaymentReleased && (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-[#008751] shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-emerald-950">
                Escrow Payment Verified (₦{agreedOrBudgetAmount.toLocaleString()}) — Work Authorized
              </h3>
              <p className="text-xs text-emerald-800 mt-0.5">
                90% (₦{proShareAmount.toLocaleString()}) is reserved for the professional upon customer completion confirmation.
              </p>
            </div>
          </div>
          {currentUser && (
            <button
              onClick={() =>
                navigateTo(currentUser.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard', {
                  jobId: job.id,
                })
              }
              className="shrink-0 px-4 py-2 rounded-xl bg-[#008751] text-white text-xs font-bold hover:bg-[#006b3f] transition"
            >
              Manage Active Job
            </button>
          )}
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 text-white p-6 sm:p-7">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold bg-[#008751] text-white">
              <Wrench className="w-3.5 h-3.5" />
              {job.categoryName || 'Home Service'}
            </span>
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase ${
                job.urgency === 'emergency'
                  ? 'bg-red-500/20 text-red-200 border border-red-400/30'
                  : 'bg-slate-800 text-slate-200'
              }`}
            >
              {job.urgency || 'Standard'}
            </span>
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase ${
                isPaymentReleased
                  ? 'bg-emerald-500/20 text-emerald-200'
                  : isEscrowFunded
                  ? 'bg-emerald-500/20 text-emerald-200'
                  : isAwaitingPayment
                  ? 'bg-amber-500/20 text-amber-200'
                  : 'bg-blue-500/20 text-blue-200'
              }`}
            >
              {isPaymentReleased
                ? 'Completed & Paid'
                : isEscrowFunded
                ? 'In Progress • Escrow Funded'
                : isAwaitingPayment
                ? 'Assigned • Awaiting Escrow Payment'
                : 'Open for Quotes'}
            </span>
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">{job.title}</h1>

          <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-slate-300">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-emerald-400" />
              {job.lga}, {job.state}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-emerald-400" />
              Posted {new Date(job.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Body Grid */}
        <div className="p-6 sm:p-7 space-y-6">
          {/* Top KPI Strip: Price, Payment State, Escrow Split */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                {effectiveCheckoutQuote ? 'Agreed Job Price' : 'Customer Target Budget'}
              </p>
              <p className="text-2xl font-extrabold text-slate-900">
                {agreedOrBudgetAmount > 0 ? `₦${agreedOrBudgetAmount.toLocaleString()}` : 'Open for Quotes'}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Payment State
              </p>
              <p
                className={`text-sm font-bold ${
                  isPaymentReleased
                    ? 'text-emerald-700'
                    : isEscrowFunded
                    ? 'text-emerald-700'
                    : isAwaitingPayment
                    ? 'text-amber-700'
                    : 'text-slate-700'
                }`}
              >
                {isPaymentReleased
                  ? 'Released to Professional'
                  : isEscrowFunded
                  ? 'Held Safely in Escrow'
                  : isAwaitingPayment
                  ? 'Awaiting Customer Escrow Payment'
                  : 'Pay Only When You Hire'}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Escrow Breakdown
              </p>
              <p className="text-xs font-semibold text-slate-700">
                90% Artisan (₦{proShareAmount.toLocaleString()}) • 10% Platform (₦{platformCommission.toLocaleString()})
              </p>
            </div>
          </div>

          {/* Job Description */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              Job Description
            </h2>
            <p className="text-slate-800 leading-relaxed whitespace-pre-line bg-slate-50 p-4 rounded-xl border border-slate-100 text-sm sm:text-base">
              {job.description}
            </p>
          </div>

          {/* Location Section */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Service Location
              </h3>
              <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-[#008751]" />
                {canViewExactAddress && job.address
                  ? `${job.address}, ${job.lga}, ${job.state}`
                  : `${job.lga}, ${job.state}`}
              </p>
              {!canViewExactAddress && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5" /> Exact street address unlocks after escrow payment is confirmed.
                </p>
              )}
            </div>
            {canViewExactAddress && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${job.address || ''} ${job.lga} ${job.state} Nigeria`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:border-[#008751] hover:text-[#008751] transition self-start sm:self-center"
              >
                Open in Maps <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>

          {/* Media Section (Photos & Videos) */}
          {((job.photos && job.photos.length > 0) || (job.videos && job.videos.length > 0)) && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                <ImageIcon className="w-4 h-4" /> Attached Photos & Videos
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(job.photos || []).map((url: string, idx: number) => (
                  <a
                    key={`img-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-xl overflow-hidden border border-slate-200 bg-slate-100 aspect-video"
                  >
                    <img src={url} alt={`Job media ${idx + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
                {(job.videos || []).map((url: string, idx: number) => (
                  <div
                    key={`vid-${idx}`}
                    className="rounded-xl overflow-hidden border border-slate-200 bg-slate-900 aspect-video relative"
                  >
                    <video src={url} controls className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold flex items-center gap-1">
                      <Video className="w-3 h-3" /> Video
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Scope Summary if available */}
          {(job.aiAnalysis?.problem_summary || job.aiScopeEstimate?.costRangeExplanation) && (
            <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-emerald-900 font-bold text-xs uppercase tracking-wider mb-1.5">
                <Sparkles className="w-4 h-4 text-[#008751]" />
                AI Scope & Diagnostic Summary
              </div>
              <p className="text-sm text-emerald-950">
                {job.aiAnalysis?.problem_summary || job.aiScopeEstimate?.costRangeExplanation}
              </p>
            </div>
          )}

          {/* Available Actions Footer */}
          <div className="pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-[#008751]" />
              <span>Protected by JobMatch NG Paystack Escrow</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Customer Actions */}
              {(!currentUser || currentUser.role === 'customer') && (
                <>
                  {isAwaitingPayment && effectiveCheckoutQuote && (
                    <button
                      onClick={() => {
                        if (!currentUser) {
                          navigateTo('customer_auth', {
                            returnView: 'job_details',
                            jobId: job.id,
                            quoteId: effectiveCheckoutQuote.id,
                            pay: 'true',
                          });
                          return;
                        }
                        setPaymentQuote(effectiveCheckoutQuote);
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#008751] text-white font-bold text-sm hover:bg-[#006b3f] transition shadow-sm"
                    >
                      <CreditCard className="w-4 h-4" />
                      Complete Escrow Payment (₦{agreedOrBudgetAmount.toLocaleString()})
                    </button>
                  )}

                  {jobQuotes.length > 0 && (
                    <button
                      onClick={() => navigateTo('quote_comparison', { jobId: job.id })}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 transition"
                    >
                      <FileText className="w-4 h-4" />
                      Compare Quotes ({jobQuotes.length})
                    </button>
                  )}
                </>
              )}

              {/* Professional Actions */}
              {(!currentUser || currentUser.role === 'professional') && (
                <>
                  {job.status === 'open' && !isAwaitingPayment && !isEscrowFunded && (
                    <>
                      <button
                        onClick={handleProfessionalAcceptJob}
                        disabled={acceptingJob}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#008751] text-white font-bold text-sm hover:bg-[#006b3f] transition shadow-sm disabled:opacity-50"
                      >
                        <UserCheck className="w-4 h-4" />
                        {acceptingJob
                          ? 'Accepting...'
                          : `Accept Job (₦${(job.budgetEstimate || 15000).toLocaleString()})`}
                      </button>
                      <button
                        onClick={() => {
                          if (!currentUser) {
                            navigateTo('pro_auth', { returnView: 'available_jobs', jobId: job.id });
                          } else {
                            navigateTo('available_jobs', { jobId: job.id });
                          }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-800 font-bold text-sm hover:bg-slate-50 transition"
                      >
                        <FileText className="w-4 h-4" />
                        Submit Custom Quote
                      </button>
                    </>
                  )}

                  {isAssignedPro && isAwaitingPayment && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-amber-100 text-amber-900 text-xs font-bold">
                      <Clock className="w-4 h-4" /> Awaiting Customer Escrow Payment — Work Locked
                    </span>
                  )}
                </>
              )}

              {currentUser && (isCustomerOwner || isAssignedPro) && (
                <button
                  onClick={() => navigateTo('messages', { jobId: job.id })}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 font-bold text-sm hover:bg-slate-100 transition"
                >
                  <MessageSquare className="w-4 h-4 text-[#008751]" />
                  Open Messages
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Paystack Escrow Payment Modal */}
      {paymentQuote && currentUser && (
        <JobPaymentModal
          isOpen={true}
          job={job}
          quote={paymentQuote}
          customerId={currentUser.id}
          customerEmail={currentUser.email}
          onClose={() => setPaymentQuote(null)}
          onPaymentSuccess={async (reference) => {
            setPaymentQuote(null);
            setJob((prev) =>
              prev
                ? {
                    ...prev,
                    status: 'in_progress',
                    paymentStatus: 'held_in_escrow',
                    payment_status: 'held_in_escrow' as any,
                    escrow_held: true,
                    payment_reference: reference,
                  }
                : prev
            );
            await refreshJobsAndQuotes().catch(() => {});
            navigateTo('customer_dashboard', {
              tab: 'active',
              activeTab: 'active',
              jobId: job.id,
              paystackRef: reference,
              paymentStatus: 'success',
            });
          }}
        />
      )}
    </div>
  );
};
